import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, HostListener, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterModule, RouterOutlet } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import * as pakageJson from '../../package.json';
import { environment } from '../environments/environment';
import { GigsumoService } from '../service/gigsumo.service';

interface MenuItem {
  label: string;
  route: string;
  active: boolean;
}

export const digitKeyOnly = (e: any) => {
  var keyCode = e.keyCode == 0 ? e.charCode : e.keyCode;
  if ((keyCode >= 37 && keyCode <= 40) || (keyCode == 8 || keyCode == 9 || keyCode == 13) || (keyCode >= 48 && keyCode <= 57)) {
    return true;
  }
  return false;
}

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, RouterModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit {
  routerEvents = new Subscription
  currentYear: any = new Date().getFullYear();
  ENVIRONMENT = environment;
  public versions:any=pakageJson.version;
  title = 'angular-routing';
  footerUrl = 'https://www.gigsumo.com';
  footerLink = 'www.gigsumo.com';
  showHeader: boolean = true
  currentPage: string = ''
  isJobCandidateListingActive: boolean = false;
  isJobDetail: boolean = false;
  isPricing: boolean = false;
  isCandidateDetail: boolean = false;
  isCompanyDirectory: boolean = false;
  isMemberDirectory: boolean = false;
  isReloading: boolean = false
  showFooter: boolean = true;

  constructor(
    private router: Router,
    private elementRef: ElementRef,
    private gigsumoService: GigsumoService,
    @Inject(PLATFORM_ID) private platformId: object

  ) {

    this.routerEvents = this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe((event: NavigationStart) => {
      // UI visibility logic
      this.showHeader = !event.url.startsWith('/web/')
      this.isJobDetail = event.url.startsWith('/jobs')
      this.isPricing = event.url.startsWith('/price')
      this.isCompanyDirectory = event.url.startsWith('/companies')
      this.isCandidateDetail = event.url.startsWith('/talents')
      this.isJobCandidateListingActive = (event.url.startsWith('/jobs') ||
        event.url.startsWith('/talents')) ||
        event.url.startsWith('/web');

      this.isMemberDirectory = event.url.startsWith('/members'); 

      // Reload logic for current URL navigation
      // if (isPlatformBrowser(this.platformId) && event.url === this.router.url) {
      //    // Add a flag to prevent potential infinite loops
      //    if (!this.isReloading) {
      //     this.isReloading = true;
      //     this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      //       this.router.navigate([event.url]).then(() => {
      //         this.isReloading = false;
      //       });
      //     });
      //   }
      // }
    });
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    this.updateActiveItem(this.router.url);
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.updateActiveItem(event.url);
    });

    // Subscribe to footer visibility changes
    this.gigsumoService.footerVisibility$.subscribe((visible) => {
      this.showFooter = visible;
    });
  }

  isMobileMenuOpen = false;
  isMobileView: boolean = false;


  /** routerLink="/" does the navigation. Already on home, the router ignores
   *  a same-URL navigation, so just go back to the top. This used to call
   *  window.location.reload() here, which re-downloaded the whole home page
   *  on every logo click — twice on a double-click. */
  goHome(): void {
    if (isPlatformBrowser(this.platformId) && this.router.url === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  showDropdown(): void {
    this.isOpen = true;
  }

  hideDropdown(): void {
    this.isOpen = false;
  }

  menuItems: MenuItem[] = [
    { label: 'Recruiters', route: 'web/recruiters.html', active: false },
    { label: 'Bench Sales', route: 'web/bench-sales.html', active: false },
    { label: 'Job Seekers', route: 'web/job-seekers.html', active: false },
    //  { label: 'Benefits', route: 'web/benefits.html', active: false },
  ];

  selectedItem: MenuItem = this.menuItems[0]; // Default selection
  isOpen: boolean = false;

  updateActiveItem(url: string): void {
    // Reset all items
    this.menuItems.forEach(item => item.active = false);

    // Find matching route
    const matchedItem = this.menuItems.find(item =>
      url.includes(item.route) ||
      (item.route === '/web/recruiters.html' && url === '/')
    );

    if (matchedItem) {
      matchedItem.active = true;
      this.selectedItem = matchedItem;
    }
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }

  selectItem(item: MenuItem): void {
    this.menuItems.forEach(i => i.active = false);
    item.active = true;
    this.selectedItem = item;
    this.isOpen = false;
  }

  /** Set by the first click on a link that makes the browser load a new
   *  page (plain href — /companies/, the /web/*.html pages). */
  private fullPageLoadPending = false;
  private fullPageLoadTimer?: ReturnType<typeof setTimeout>;

  /**
   * Double-click guard for full page loads. The second click of a
   * double-click on a plain href cancels the load the first one started and
   * starts it again, so the page reloads and takes longer. While a load is
   * pending, further link clicks are swallowed.
   *
   * routerLink clicks never reach the guard: RouterLink calls
   * preventDefault() on its own click before this document listener runs,
   * and a same-URL router navigation is already ignored.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (event.defaultPrevented || event.button !== 0 ||
        event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    const link = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!link || link.hasAttribute('download') ||
        (link.target && link.target !== '_self')) {
      return;
    }

    const href = link.getAttribute('href') || '';
    if (href.startsWith('#') || href.startsWith('javascript:') ||
        href.startsWith('mailto:') || href.startsWith('tel:')) {
      return;
    }

    if (this.fullPageLoadPending) {
      event.preventDefault();
      return;
    }

    this.fullPageLoadPending = true;
    // If the visitor stops the load (Esc / stop button) the page stays, so
    // links must work again. Long enough for a slow load on QA.
    clearTimeout(this.fullPageLoadTimer);
    this.fullPageLoadTimer = setTimeout(() => this.fullPageLoadPending = false, 10000);
  }

  /** Back/forward can restore this page from the browser's cache with the
   *  flag still set — clear it so the links aren't dead. */
  @HostListener('window:pageshow')
  onPageShow(): void {
    this.fullPageLoadPending = false;
    clearTimeout(this.fullPageLoadTimer);
  }
}
